import { cur } from '@/lib/currency';
import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Sparkles, X, Banknote, Truck, Users } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

/**
 * PlatformIbanBanner — iter400 / generalized iter401.
 *
 * Surfaces "Detected new IBAN" suggestions across platforms,
 * suppliers, and customers. For every (entity_type, entity_id, iban)
 * the system finds an unmatched bank txn whose description references
 * the entity's name / name_ar / alt_names, this banner offers a
 * one-click "Add IBAN" so future settlements auto-link.
 *
 * UX:
 *   - Compact card, one row per suggestion (icon picks per entity_type).
 *   - "Add IBAN" → confirm dialog with sample txn details.
 *   - "X" → persistent dismissal stored in `platform_iban_dismissals`.
 *
 * Renders nothing when the list is empty so it's safe to mount on
 * every banking page.
 */
const SR = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ENTITY_META = {
  platform: { Icon: Banknote, label: 'platform', tone: 'cyan' },
  supplier: { Icon: Truck, label: 'supplier', tone: 'amber' },
  customer: { Icon: Users, label: 'customer', tone: 'emerald' },
};

export function PlatformIbanBanner({
  days = 90,
  testidPrefix = 'plat-iban',
  entityTypes = 'platform,supplier,customer',
  entityId = null,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/banking/iban-suggestions?entity_types=${entityTypes}&days=${days}`);
      const all = Array.isArray(res.data) ? res.data : [];
      // iter405 — entityId filter for embedded use inside a single
      // entity's detail / ledger dialog (e.g. "show me only THIS
      // supplier's unmatched IBANs").
      setItems(entityId ? all.filter((s) => s.entity_id === entityId) : all);
    } catch {
      setItems([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days, entityTypes, entityId]);

  const handleAdd = async () => {
    if (!confirming) return;
    setSubmitting(true);
    try {
      await api.post('/banking/iban-suggestions/add', {
        entity_type: confirming.entity_type,
        entity_id: confirming.entity_id,
        iban: confirming.iban,
      });
      toast.success(`IBAN added to ${confirming.entity_name} — future bank txns will auto-link`);
      setConfirming(null);
      setItems((prev) => prev.filter((x) => !(
        x.entity_type === confirming.entity_type &&
        x.entity_id === confirming.entity_id &&
        x.iban === confirming.iban
      )));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add IBAN');
    } finally { setSubmitting(false); }
  };

  const handleDismiss = async (sug) => {
    try {
      await api.post('/banking/iban-suggestions/dismiss', {
        entity_type: sug.entity_type,
        entity_id: sug.entity_id,
        iban: sug.iban,
      });
      setItems((prev) => prev.filter((x) => !(
        x.entity_type === sug.entity_type &&
        x.entity_id === sug.entity_id &&
        x.iban === sug.iban
      )));
    } catch {
      toast.error('Failed to dismiss');
    }
  };

  if (!loading && items.length === 0) return null;
  if (loading && items.length === 0) return null;

  return (
    <>
      <Card
        className="border-cyan-200 bg-gradient-to-br from-cyan-50 to-sky-50 dark:from-cyan-950/30 dark:to-sky-950/20"
        data-testid={`${testidPrefix}-banner`}
      >
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center gap-2">
            <Sparkles size={14} className="text-cyan-600" />
            <span className="text-xs font-bold uppercase tracking-wider text-cyan-800">
              {items.length} new IBAN{items.length === 1 ? '' : 's'} detected
            </span>
            <span className="text-[10px] text-cyan-700/70 ml-1 normal-case font-normal">
              — teach the system once, every future bank txn auto-links
            </span>
          </div>
          <div className="space-y-1.5">
            {items.map((s) => {
              const meta = ENTITY_META[s.entity_type] || ENTITY_META.platform;
              const Icon = meta.Icon;
              return (
                <div
                  key={`${s.entity_type}::${s.entity_id}::${s.iban}`}
                  className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-white/60 dark:bg-black/20 border border-cyan-200/60"
                  data-testid={`${testidPrefix}-row-${s.entity_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold flex items-center gap-2 flex-wrap">
                      <Icon size={12} className="text-cyan-700 shrink-0" />
                      <span>{s.entity_name}</span>
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted/70 text-muted-foreground font-medium">
                        {meta.label}
                      </span>
                      <span className="font-mono text-[10px] text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">
                        {s.iban}
                      </span>
                      {s.txn_count > 1 && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-cyan-200 text-cyan-900 font-bold">
                          {s.txn_count}× seen
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5 truncate" title={s.sample_description}>
                      Last: {cur()} {SR(s.sample_amount)} · {s.sample_date || '—'} · "{s.sample_description}"
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Button
                      size="sm"
                      onClick={() => setConfirming(s)}
                      className="h-7 text-[11px] px-2.5 bg-cyan-600 hover:bg-cyan-700"
                      data-testid={`${testidPrefix}-add-${s.entity_id}`}
                    >
                      Add IBAN
                    </Button>
                    <button
                      type="button"
                      onClick={() => handleDismiss(s)}
                      className="h-7 w-7 rounded-md hover:bg-muted flex items-center justify-center text-muted-foreground"
                      title="Dismiss this suggestion"
                      data-testid={`${testidPrefix}-dismiss-${s.entity_id}`}
                    >
                      <X size={13} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!confirming} onOpenChange={(o) => !o && setConfirming(null)}>
        <DialogContent className="max-w-md" data-testid={`${testidPrefix}-confirm-dialog`}>
          <DialogHeader>
            <DialogTitle>Add IBAN to {confirming?.entity_name}?</DialogTitle>
            <DialogDescription>
              Once linked, every future bank-statement transfer matching this IBAN will auto-match to {confirming?.entity_name} ({confirming?.entity_type}) in the Quick-Link inbox.
            </DialogDescription>
          </DialogHeader>
          {confirming && (
            <div className="space-y-2 py-2">
              <div className="rounded-lg border border-border p-3 bg-muted/30 text-sm space-y-1">
                <div className="flex justify-between gap-2">
                  <span className="text-[11px] uppercase tracking-wider text-muted-foreground">IBAN</span>
                  <span className="font-mono text-xs font-semibold">{confirming.iban}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Last txn</span>
                  <span className="text-xs text-right max-w-[260px] truncate" title={confirming.sample_description}>
                    {confirming.sample_description || '—'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 pt-1 border-t border-border/60 mt-1">
                  <div>
                    <div className="text-[10px] text-muted-foreground">Amount</div>
                    <div className="font-mono font-semibold text-sm">{cur()} {SR(confirming.sample_amount)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-muted-foreground">Date</div>
                    <div className="text-sm">{confirming.sample_date || '—'}</div>
                  </div>
                </div>
                {confirming.txn_count > 1 && (
                  <p className="text-[11px] text-cyan-700 pt-1">
                    Detected on <b>{confirming.txn_count}</b> separate txns — strong match.
                  </p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(null)} disabled={submitting} data-testid={`${testidPrefix}-confirm-cancel`}>
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={submitting}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid={`${testidPrefix}-confirm-add`}
            >
              {submitting ? 'Adding…' : 'Add IBAN'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default PlatformIbanBanner;
